/* ═══════════════════════════════════════════════════════════════
   FormaPlaceholderBindings — the "Project Tokens" palette of a
   PROJECT forma copy: one row per blank the master left behind.

   Form (DESIGN.md §3.2): a stacked LIST, one card per placeholder,
   carrying the same four facts the contract sketched as columns —
   Token · In the template · Bind to · Preview. A real 4-column
   table needs ~700px; this lives in a docked side rail at ~300–420,
   where a table would scroll sideways (§3.1 forbids that). Stacked,
   each fact keeps its own label and the whole set stays scannable
   top-to-bottom, which is how you actually work a checklist of
   "which ones are still blank?".
   ═══════════════════════════════════════════════════════════════ */
import React from 'react';
import type { FormaBinding, StandardConfig } from '../../shared/types';
import {
  ISSUANCE_DATE_ID,
  placeholderLabel,
  resolvePlaceholders,
  usedPlaceholderIds,
  type PlaceholderContext,
} from '../../shared/forma-placeholders';
import { PROJECT_TOKENS } from '../constants';
import { SearchableSelect } from './SearchableSelect';
import { Input } from './ui/input';
import { Hint } from './ui/tooltip';
import { FORMA_TIPS } from '../formaTooltips';
import EmptyState from './EmptyState';
import { rem } from '../lib/rem';

const KIND_OPTIONS = [
  { value: 'field', label: 'Project field' },
  { value: 'issuance', label: 'Issuance' },
  { value: 'text', label: 'Typed text' },
];

const FIELD_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: 'var(--tm)',
  display: 'block',
  // eslint-disable-next-line sf/no-raw-px -- 4px, below the >4 conversion threshold
  marginBottom: 4,
};

export function FormaPlaceholderBindings({
  config,
  ctx,
  issuanceNames,
  onChange,
}: {
  config: StandardConfig;
  /** Live project/issuance data, so the Preview line shows real values. */
  ctx: PlaceholderContext;
  /** Issuance names in this project (deliverables + frozen issuances), newest last. */
  issuanceNames: string[];
  onChange: (bindings: Record<string, FormaBinding>) => void;
}) {
  const ids = usedPlaceholderIds(config);
  const bindings = config.bindings || {};
  // A deliverable IS the issuance: unissued ones say so in the row.
  const upcoming = new Set(
    (ctx.issuances || []).filter((i) => i.issued === false).map((i) => i.name || '')
  );
  const resolved = resolvePlaceholders(config, ctx);

  const set = (id: string, patch: Partial<FormaBinding>) => {
    const cur: FormaBinding = bindings[id] || {
      kind: id === ISSUANCE_DATE_ID ? 'issuance' : 'field',
      value: '',
    };
    onChange({ ...bindings, [id]: { ...cur, ...patch } });
  };

  if (ids.length === 0) {
    return (
      <div style={{ padding: 'var(--space-3)' }}>
        <EmptyState
          title="No placeholders in this forma"
          subtitle="Drag the Placeholder chip above onto the page and it shows up here."
        />
      </div>
    );
  }

  const unbound = ids.filter((id) => !resolved[id]);

  return (
    /* Natural height — this list is the last section flowing inside the
       Tokens palette's single scroll container (Josh, 2026-08-26). */
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Status first: the one thing you came here to find out. */}
      <div
        style={{
          padding: `${rem(10)} var(--space-3)`,
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          lineHeight: 1.5,
          color: unbound.length ? 'var(--tx)' : 'var(--tm)',
          borderBottom: '1px solid var(--bd)',
          background: unbound.length
            ? 'color-mix(in srgb, var(--color-warning) 12%, transparent)'
            : 'transparent',
        }}
        role="status"
        aria-live="polite"
      >
        {unbound.length
          ? `${unbound.length} placeholder${unbound.length > 1 ? 's' : ''} still need${
              unbound.length > 1 ? '' : 's'
            } a value — ${unbound.map(placeholderLabel).join(', ')}. Specs can't be exported until every one is set.`
          : 'Every placeholder in this forma has a value. Specs are ready to export.'}
      </div>

      <div
        className="sf-palette-flow"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-3)',
        }}
      >
        {ids.map((id) => {
          const b = bindings[id];
          const kind = b?.kind || (id === ISSUANCE_DATE_ID ? 'issuance' : 'field');
          const preview = resolved[id] || '';
          const was = config.placeholders?.[id]?.was;
          const textId = `ph-text-${id}`;
          return (
            <div
              key={id}
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--sf)',
                padding: 'var(--space-3)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              {/* Token + what the template had there */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontWeight: 700,
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tx)',
                    flexShrink: 0,
                  }}
                >
                  {placeholderLabel(id)}
                </span>
                <Hint text={FORMA_TIPS.bindingWas}>
                  <span
                    tabIndex={0}
                    style={{
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      color: 'var(--tm)',
                      fontStyle: was ? 'italic' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {was ? `was “${was}”` : 'no memory from the template'}
                  </span>
                </Hint>
              </div>

              {/* Bind to */}
              <div>
                <Hint text={FORMA_TIPS.bindingKind}>
                  <span tabIndex={0} style={FIELD_LABEL}>
                    Bind to
                  </span>
                </Hint>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <SearchableSelect
                    value={kind}
                    options={
                      id === ISSUANCE_DATE_ID
                        ? KIND_OPTIONS.filter((o) => o.value !== 'field')
                        : KIND_OPTIONS
                    }
                    onChange={(v) =>
                      set(id, { kind: (v || 'field') as FormaBinding['kind'], value: '' })
                    }
                    searchPlaceholder="Search…"
                    clearable={false}
                  />
                  {kind === 'field' && (
                    <Hint text={FORMA_TIPS.bindingField}>
                      <div>
                        <SearchableSelect
                          value={b?.value || ''}
                          options={PROJECT_TOKENS.filter((t) => t.value !== '{IssuanceDate}').map(
                            (t) => ({ value: t.value, label: t.label })
                          )}
                          onChange={(v) => set(id, { value: v || '' })}
                          placeholder="Pick a project field…"
                          searchPlaceholder="Search fields…"
                        />
                      </div>
                    </Hint>
                  )}
                  {kind === 'issuance' && (
                    <Hint text={FORMA_TIPS.bindingIssuance}>
                      <div>
                        <SearchableSelect
                          value={b?.value || ''}
                          options={[
                            { value: '', label: 'Latest issuance' },
                            ...issuanceNames.map((n) => ({
                              value: n,
                              label: n,
                              ...(upcoming.has(n) ? { hint: 'Upcoming' } : {}),
                            })),
                          ]}
                          onChange={(v) => set(id, { value: v || '' })}
                          searchPlaceholder="Search issuances…"
                          clearable={false}
                        />
                        {issuanceNames.length === 0 && (
                          <div
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--tm)',
                              marginTop: 'var(--space-1)',
                            }}
                          >
                            No deliverables yet — add one on the project dashboard.
                          </div>
                        )}
                      </div>
                    </Hint>
                  )}
                  {kind === 'text' && (
                    <>
                      <label htmlFor={textId} className="sr-only">
                        Text for {placeholderLabel(id)}
                      </label>
                      <Hint text={FORMA_TIPS.bindingText}>
                        <Input
                          id={textId}
                          value={b?.value || ''}
                          placeholder="Text to print"
                          onChange={(e) => set(id, { value: e.target.value })}
                        />
                      </Hint>
                    </>
                  )}
                </div>
              </div>

              {/* Preview — the answer, so it reads last */}
              <div>
                <Hint text={FORMA_TIPS.bindingPreview}>
                  <span tabIndex={0} style={FIELD_LABEL}>
                    Preview
                  </span>
                </Hint>
                <div
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    lineHeight: 1.4,
                    // danger, not warning: --color-warning is 2.2:1 on the light
                    // surface (§7 needs 4.5:1 for body text), and an unbound
                    // blank genuinely blocks the export.
                    color: preview ? 'var(--tx)' : 'var(--color-danger)',
                    fontWeight: preview ? 500 : 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(6),
                  }}
                >
                  {/* Never color alone (§7): unset also says so in words. */}
                  {preview || 'Not set — this spec can’t export yet'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
